import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LLAPTributePage = () => {
  const [formData, setFormData] = useState({
    yourName: '',
    yourEmail: '',
    nomineeName: '',
    nomineeEmail: '',
    organization: '',
    reason: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/nominations`, {
        your_name: formData.yourName,
        your_email: formData.yourEmail,
        nominee_name: formData.nomineeName,
        nominee_email: formData.nomineeEmail,
        organization: formData.organization,
        reason: formData.reason
      });
      
      setFormSubmitted(true);
      setFormData({
        yourName: '',
        yourEmail: '',
        nomineeName: '',
        nomineeEmail: '',
        organization: '',
        reason: ''
      });
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (err) {
      console.error('Error submitting nomination:', err);
      setError('Failed to submit nomination. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">Live Long & Prosper </span>
            <span className="text-amber-500">Tribute Award</span>
          </h1>
          <p className="text-gray-300 text-lg">
            Honoring individuals and organizations that embody the spirit of Leonard Nimoy's message of hope, logic, and service to others.
          </p>
        </div>
      </section>

      {/* About the Award */}
      <section className="py-16 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
            About the Award
          </span>
          <h2 className="text-3xl font-bold text-white mt-4 mb-6">
            Celebrating Excellence
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            The Live Long & Prosper Tribute Award recognizes those who make extraordinary contributions to their communities, embodying the values that Leonard Nimoy championed throughout his life.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Recipients are chosen for their commitment to making the world a better place, whether through scientific advancement, community service, autism advocacy, or promoting diversity and inclusion.
          </p>
          <div className="text-6xl mt-8">🖖</div>
          <p className="text-amber-500 font-semibold mt-2">LLAP</p>
        </div>
      </section>

      {/* IDIC Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-6">🖖</div>
          <h2 className="text-3xl font-bold text-white mb-6">
            IDIC: Infinite Diversity in Infinite Combinations
          </h2>
          <p className="text-gray-400 leading-relaxed">
            A Vulcan philosophy that celebrates the vast variety of the universe. This principle, central to Spock's character, reminds us that diversity is not just to be tolerated — it is to be celebrated as the source of all beauty and strength.
          </p>
        </div>
      </section>

      {/* Nomination Form */}
      <section id="nominate" className="py-24 bg-[#0a0a0b]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Nominate a Future Honoree
            </h2>
            <p className="text-gray-400">
              We are looking for autistic leaders and allies committed to creating a world that embraces neurodiversity and belonging for all.
            </p>
          </div>

          {formSubmitted ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-8 text-center">
              <p className="text-green-500 text-lg font-semibold">Thank you for your nomination!</p>
              <p className="text-gray-400 mt-2">We will review your submission shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                  <p className="text-red-500">{error}</p>
                </div>
              )}
              {/* Your Information */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Your Information</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="yourName" className="block text-sm text-gray-400 mb-2">Your Name *</label>
                    <input
                      type="text"
                      id="yourName"
                      name="yourName"
                      value={formData.yourName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="yourEmail" className="block text-sm text-gray-400 mb-2">Your Email *</label>
                    <input
                      type="email"
                      id="yourEmail"
                      name="yourEmail"
                      value={formData.yourEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Nominee Information */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Nominee Information</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nomineeName" className="block text-sm text-gray-400 mb-2">Nominee's Name *</label>
                    <input
                      type="text"
                      id="nomineeName"
                      name="nomineeName"
                      value={formData.nomineeName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="nomineeEmail" className="block text-sm text-gray-400 mb-2">Nominee's Email *</label>
                    <input
                      type="email"
                      id="nomineeEmail"
                      name="nomineeEmail"
                      value={formData.nomineeEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="organization" className="block text-sm text-gray-400 mb-2">Organization</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="reason" className="block text-sm text-gray-400 mb-2">Why are you nominating this person? *</label>
                    <textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-[#141416] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors resize-none"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Nomination'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LLAPTributePage;
